import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import ThreadConnector from "@/assets/icons/agents-targets/threaded-targets/targets-thread";
import AvatarIcon from "@/assets/icons/agents-targets/icons/AvatarIcon";
import PlusExpandIcon from "@/assets/icons/agents-targets/icons/PlusExpandIcon";
import MinusCollapseIcon from "@/assets/icons/agents-targets/icons/MinusCollapseIcon";
import ThreadConnectorNode from "./thread-connector-node";
import { AvatarWithConnectorsProps, ConnectorPosition } from "../types";
import { useAvatarTree } from "../hooks/avatar-tree.hook";
import { useAgentsTargets } from "@/modules/agents/hooks/agents-targets.hook";
import { AgentTargetDTO } from "@/modules/agents/dtos/index.v2";
import {
  calculateDynamicPositions,
  calculateChildrenPositions,
  calculateThreadHeight,
} from "../utils";
import TargetCard from "@/modules/targetsv2/components/target-card";
import { transformToTargetCardData } from "@/modules/targetsv2/hooks/targets-page.hook";

const AvatarWithConnectors: React.FC<AvatarWithConnectorsProps> = ({
  username,
  connectors = [],
  hasReports = false,
  cognitoId = "",
  color,
  startDate = "",
  endDate = "",
  onThreadHeightChange,
  targetsData,
  user,
  quarter,
  year,
}) => {
  const [dynamicConnectors, setDynamicConnectors] =
    useState<ConnectorPosition[]>(connectors);
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [nodeCognitoIds, setNodeCognitoIds] = useState<Map<string, string>>(
    new Map([[username, cognitoId]])
  );

  const {
    expandedStates,
    hoveredStates,
    handleExpandClick,
    handleMouseEnter,
    handleMouseLeave,
  } = useAvatarTree(username, dynamicConnectors);

  // API call for fetching children
  const [fetchingCognitoId, setFetchingCognitoId] = useState<string | null>(
    null
  );

  const { data: childrenData } = useAgentsTargets({
    query: {
      from: startDate,
      to: endDate,
      userUId: fetchingCognitoId || "",
      includeDirectReports: true,
    },
    enabled: !!fetchingCognitoId,
  });

  // Transform AgentTargetDTO to ConnectorPosition format
  const transformAgentToConnector = useCallback(
    (agent: AgentTargetDTO, position: number): ConnectorPosition => {
      // Transform target data to sales data format
      const transformedTargetsData = transformToTargetCardData(agent);

      const newConnector = {
        position,
        username: agent.user.fullName || agent.user.name,
        hasChildren: agent.hasReports,
        children: [], // Will be populated when expanded
        targetsData: transformedTargetsData,
      };

      // Store the mapping between username and cognitoId
      setNodeCognitoIds((prev) =>
        new Map(prev).set(newConnector.username, agent.user.cognitoId)
      );

      return newConnector;
    },
    []
  );

  // Handle fetched children data
  useEffect(() => {
    if (childrenData?.data && fetchingCognitoId) {
      const children = childrenData.data[0]?.directReports || [];

      // Transform children with initial positions
      const transformedChildren = children.map((child, index) =>
        transformAgentToConnector(child, 40 + index * 120)
      );

      // Update the specific node's children
      const updateNodeChildren = (
        nodes: ConnectorPosition[],
        targetCognitoId: string
      ): ConnectorPosition[] => {
        return nodes.map((node) => {
          const nodeCognitoId = nodeCognitoIds.get(node.username);
          if (nodeCognitoId === targetCognitoId) {
            // This is the target node - update its children
            return { ...node, children: transformedChildren };
          } else if (node.children) {
            // Recursively update children
            return {
              ...node,
              children: updateNodeChildren(node.children, targetCognitoId),
            };
          }
          return node;
        });
      };

      setDynamicConnectors((prev) => {
        // If this is the main node
        if (fetchingCognitoId === cognitoId) {
          return transformedChildren;
        }
        // Otherwise update within the tree
        return updateNodeChildren(prev, fetchingCognitoId);
      });

      setLoadingNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fetchingCognitoId);
        return newSet;
      });
      setFetchingCognitoId(null);
    }
  }, [
    childrenData,
    fetchingCognitoId,
    transformAgentToConnector,
    cognitoId,
    nodeCognitoIds,
  ]);

  // Enhanced expand click handler
  const handleEnhancedExpandClick = useCallback(
    (nodeName: string) => {
      // Call the original expand logic
      handleExpandClick(nodeName);

      // Get the cognitoId for this node
      const nodeCognitoId = nodeCognitoIds.get(nodeName);

      // If expanding and node has reports, fetch children
      if (nodeCognitoId && !expandedStates[nodeName]) {
        // Check if this node has children (either hasReports for main node or hasChildren for child nodes)
        const nodeHasChildren =
          nodeName === username
            ? hasReports
            : findNodeHasChildren(dynamicConnectors, nodeName);

        if (nodeHasChildren) {
          setLoadingNodes((prev) => new Set(prev).add(nodeCognitoId));
          setFetchingCognitoId(nodeCognitoId);
        }
      }
    },
    [
      handleExpandClick,
      nodeCognitoIds,
      expandedStates,
      username,
      hasReports,
      dynamicConnectors,
    ]
  );

  // Helper function to find if a node has children
  const findNodeHasChildren = (
    nodes: ConnectorPosition[],
    targetName: string
  ): boolean => {
    for (const node of nodes) {
      if (node.username === targetName) {
        return node.hasChildren || false;
      }
      if (node.children) {
        const result = findNodeHasChildren(node.children, targetName);
        if (result) return result;
      }
    }
    return false;
  };

  const isMainExpanded = expandedStates[username];
  const isMainHovered = hoveredStates[username];
  const isMainLoading = loadingNodes.has(cognitoId);

  // Check if this user has dual targets (both team and personal with non-zero values)
  const hasDualTargets = useMemo(() => {
    if (!targetsData || targetsData.length === 0) return false;
    const hasTeam = targetsData.some(
      (d) =>
        d.targetType === "team" && parseFloat(d.target.replace(/,/g, "")) > 0
    );
    const hasPersonal = targetsData.some(
      (d) =>
        d.targetType === "personal" &&
        (parseFloat(d.target.replace(/,/g, "")) > 0 ||
          parseFloat(d.current.replace(/,/g, "")) > 0)
    );
    return hasTeam && hasPersonal;
  }, [targetsData]);

  const threadHeight = calculateThreadHeight(
    dynamicConnectors,
    expandedStates,
    isMainExpanded,
    hasDualTargets
  );

  const dynamicConnectorsPositioned = isMainExpanded
    ? calculateDynamicPositions(
        calculateChildrenPositions(dynamicConnectors, hasDualTargets),
        expandedStates
      )
    : [];

  // Helper function to calculate the space needed by the last child recursively
  const calculateLastChildSpace = useCallback(
    (
      connectors: ConnectorPosition[],
      expandedStates: Record<string, boolean>
    ): number => {
      if (connectors.length === 0) return 0;

      const lastChild = connectors[connectors.length - 1];
      const isLastChildExpanded = expandedStates[lastChild.username];

      if (
        !isLastChildExpanded ||
        !lastChild.children ||
        lastChild.children.length === 0
      ) {
        return 0; // No extra space needed
      }

      // Base space for this child's children
      const childrenCount = lastChild.children.length;
      const baseSpace = childrenCount * 120; // MIN_SPACING

      // Recursively calculate space for the last child's last child
      const nestedSpace = calculateLastChildSpace(
        lastChild.children,
        expandedStates
      );

      return baseSpace + nestedSpace + 80; // Add base height for the child itself
    },
    []
  );

  // Calculate the actual visual space needed (not just thread height)
  const actualVisualHeight = useMemo(() => {
    if (!isMainExpanded || dynamicConnectors.length === 0) {
      return threadHeight; // Use thread height for collapsed nodes
    }

    // Calculate the extra space needed by the last child and its descendants
    const extraSpaceForLastChild = calculateLastChildSpace(
      dynamicConnectors,
      expandedStates
    );

    if (extraSpaceForLastChild > 0) {
      return threadHeight + extraSpaceForLastChild;
    }

    // For other expansions, use normal thread height
    return threadHeight;
  }, [
    threadHeight,
    isMainExpanded,
    dynamicConnectors,
    expandedStates,
    calculateLastChildSpace,
  ]);

  // Notify parent of actual visual height changes (not just thread height)
  React.useEffect(() => {
    if (onThreadHeightChange) {
      onThreadHeightChange(actualVisualHeight, cognitoId);
    }
  }, [actualVisualHeight, onThreadHeightChange, cognitoId]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        minHeight: "100%",
        maxWidth: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "start" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            zIndex: 10,
          }}
          onMouseEnter={(event) => {
            event.stopPropagation();
            handleMouseEnter(username, true);
          }}
          onMouseOver={(event) => {
            event.stopPropagation();
            handleMouseEnter(username, true);
          }}
          onMouseLeave={() => handleMouseLeave(username)}
        >
          <AvatarIcon width={32} height={32} backgroundColor={color} />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            justifyContent: "start",
          }}
        >
          <TargetCard
            name={username}
            targetsData={targetsData}
            user={user}
            quarter={quarter}
            year={year}
          />
        </Box>
      </Box>

      {(hasReports || dynamicConnectors.length > 0) && (
        <Box
          sx={{
            position: "absolute",
            left: "15px",
            top: 32,
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              pointerEvents: "auto",
              "& > svg path": {
                transition: "stroke 0.2s ease-in-out",
              },
              "&:hover > svg path": {
                stroke: "#015C9A",
              },
            }}
          >
            <ThreadConnector
              height={threadHeight}
              stroke={isMainHovered ? "#015C9A" : "#DCE4E9"}
              showConnector={!isMainExpanded || dynamicConnectors.length === 0}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: isMainExpanded ? "-15px" : "0px",
              top: isMainExpanded ? "20px" : `${threadHeight - 13}px`,
              zIndex: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              transition: "all 0.3s ease-in-out",
              pointerEvents: "auto",
              "&:hover": {
                transform: "scale(1.1)",
              },
            }}
            onClick={() => handleEnhancedExpandClick(username)}
          >
            <Box sx={{ ml: 1 }}>
              {isMainLoading ? (
                <CircularProgress size={16} />
              ) : isMainExpanded ? (
                <MinusCollapseIcon width={16} height={16} />
              ) : (
                <PlusExpandIcon width={16} height={16} />
              )}
            </Box>
          </Box>

          {/* Child nodes */}
          <Box
            sx={{
              opacity: isMainExpanded ? 1 : 0,
              visibility: isMainExpanded ? "visible" : "hidden",
              transition:
                "opacity 0.3s ease-in-out, visibility 0.3s ease-in-out",
              pointerEvents: "auto",
            }}
          >
            {dynamicConnectorsPositioned.map((connector, index) => (
              <ThreadConnectorNode
                key={`${connector.username}-${index}`}
                top={connector.position}
                username={connector.username}
                hasChildren={connector.hasChildren}
                children={connector.children}
                isExpanded={expandedStates[connector.username]}
                onExpandClick={() =>
                  handleEnhancedExpandClick(connector.username)
                }
                expandedStates={expandedStates}
                onChildExpandClick={handleEnhancedExpandClick}
                parentHovered={isMainHovered}
                hoveredStates={hoveredStates}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                isLoading={loadingNodes.has(
                  nodeCognitoIds.get(connector.username) || ""
                )}
                loadingNodes={loadingNodes}
                nodeCognitoIds={nodeCognitoIds}
                targetsData={connector.targetsData}
                user={user}
                quarter={quarter}
                year={year}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AvatarWithConnectors;
